import partition from 'lodash/partition';
import { useMemo } from 'react';

import { useDiagonalCorners } from '~/api/easy-estimator-api/diagonal-corners-api/useDiagonalCorners';
import { SELECTION_CIRCLES } from '~/core/constants';
import { CircleAction, DiagonalCornerOptions, ShapeType } from '~/core/enums';
import { getCornerType } from '~/core/helpers';
import { DiagonalCornerModel } from '~/core/models';
import { incById } from '~/core/utils';
import { useAppDispatch } from '~/redux/hooks';
import { updateSelectedDiagonalCorner } from '~/redux/slices/diagonalCorner';
import { checkInteriorDiagonalCornerInShapes } from '~/redux/slices/diagonalCorner/diagonalCorner.utils';
import { updateSelectionCircles } from '~/redux/slices/fabric';
import { useDrawingShapeContext } from '~contexts';

export const useSidePanelDiagonalCornerTab = () => {
  const dispatch = useAppDispatch();
  const {
    defaultValue,
    isFetchingDiagonalCorners,
    diagonalCorners: allDiagonalCorners,
  } = useDiagonalCorners();
  const {
    getters: { drawingShapes },
  } = useDrawingShapeContext();
  const actualDrawingShapes = drawingShapes.filter(
    (shape) => shape.type !== ShapeType.FAUX_SHAPE
  );
  const isInteriorDiagonalCornerEnabled = useMemo(
    () => checkInteriorDiagonalCornerInShapes(actualDrawingShapes),
    [actualDrawingShapes]
  );
  const [[_defaultOption], diagonalCorners] = partition(
    allDiagonalCorners,
    (dc) => getCornerType(dc) === DiagonalCornerOptions.REMOVE_DIAGONAL_CORNER
  );
  const optionsCounters = useMemo(
    () =>
      actualDrawingShapes.reduce(
        (z, shape) =>
          Object.values(shape.cornerMap).reduce<Record<string, number>>(
            (counter, { label, isVisibleInCanvas }) =>
              incById(counter, isVisibleInCanvas && label),
            z
          ),
        {}
      ),
    [actualDrawingShapes]
  );

  const handleUpdate =
    (action: CircleAction) => (diagonalCorner: DiagonalCornerModel) => {
      dispatch(updateSelectedDiagonalCorner(diagonalCorner));
      dispatch(
        updateSelectionCircles({
          selection: SELECTION_CIRCLES.FOR_DIAGONAL_CORNERS,
          action,
        })
      );
    };

  return {
    isFetchingDiagonalCorners,
    diagonalCorners,
    defaultOption: defaultValue,
    handleUpdate,
    optionsCounters,
    isInteriorDiagonalCornerEnabled,
  };
};
